import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../../components/button";
import FitnessToolsResultCard from "../../../components/cards/FitnessToolsResultCard";
import Footer from "../../../components/footer";
import Header from "../../../components/header";
import Input from "../../../components/input";
import Loader from "../../../components/loader";
import OtherFitnessTestSection from "../../../components/OtherFitnessTestSection";
import AISkeleton from "../../../components/skeleton/AISkeleton";
import { useTranslate } from "../../../context/TranslationContext";
import useChartPeriod from "../../../hooks/useChartPeriod";
import {
  useCreateCardioMutation,
  useGetCardioQuery,
  useUpdateCardioMutation,
} from "../../../redux/Apis/FitnessTest/Cardio";
import { useCreateAISuggestionMutation } from "../../../redux/Apis/TrackLecture";
import { useStaticData } from "../../../utils/data";
import { formatAISuggestion } from "../../../utils/helper";

const CardioFitnessTest = () => {
  const { t } = useTranslate("cardioFitnessTest");
  const { FITNESSTEST } = useStaticData();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authSlice);
  const lang = useSelector((state) => state.language.lang);
  const [hrResting, setHrResting] = useState("");
  const [hrPeak, setHrPeak] = useState("");
  const [hrOneMin, setHrOneMin] = useState("");
  const [vo2Max, setVo2Max] = useState(null);
  const [recoveryScore, setRecoveryScore] = useState(null);
  const [recoveryQuality, setRecoveryQuality] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [aiSuggestion, setAiSuggestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [highlightAI, setHighlightAI] = useState(false);
  const aiRef = useRef(null);
  const [createAISuggestion] = useCreateAISuggestionMutation();
  const [createCardio] = useCreateCardioMutation();
  const [updateCardio] = useUpdateCardioMutation();
  const {
    data: cardioHistoryData,
    refetch: refetchCardio,
    isLoading,
  } = useGetCardioQuery({ lang }, { skip: !user });

  const { period, dateRange, handlePeriodChange } = useChartPeriod();
  const { startDate, endDate } = dateRange;
  const {
    data: cardioData,
    refetch: refetchCardioData,
    isLoading: loadingCardio,
  } = useGetCardioQuery(
    { lang, startdate: startDate, enddate: endDate },
    { skip: !startDate || !endDate || !user },
  );
  const courseState = location?.state || {};
  // const templateId = courseState?.templateId;
  const courseId = courseState?.course;
  const sectionId = courseState?.sectionId || courseState?.section;
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    if (aiLoading) {
      const timer = setTimeout(() => setShowSkeleton(true), 200);
      return () => clearTimeout(timer);
    } else {
      setShowSkeleton(false);
    }
  }, [aiLoading]);

  useEffect(() => {
    if ((aiLoading || aiSuggestion) && aiRef.current) {
      setTimeout(() => {
        const y =
          aiRef.current.getBoundingClientRect().top + window.pageYOffset - 110; // header height offset

        window.scrollTo({
          top: y,
          behavior: "smooth",
        });

        setHighlightAI(true);
        setTimeout(() => setHighlightAI(false), 3000);
      }, 300);
    }
  }, [aiLoading, aiSuggestion]);

  useEffect(() => {
    if (user) {
      refetchCardio();
    }
  }, [user, refetchCardio]);

  useEffect(() => {
    if (startDate && endDate && user) {
      refetchCardioData();
    }
  }, [startDate, endDate, user, refetchCardioData]);

  // useEffect(() => {
  //   setMetaTags(
  //     "Traineego | Buy or Create Automated Courses & Tracks for Fitness & Wellness",
  //     "Create, manage, and sell your own courses and tracks, or buy high-quality content. Empower your career through automation and passive income today!",
  //   );
  // }, []);

  useEffect(() => {
    if (cardioHistoryData) {
      setHistoryData(cardioHistoryData.data || []);
    }
  }, [cardioHistoryData]);

  const calculateCardioFitness = () => {
    const rest = parseFloat(hrResting);
    const peak = parseFloat(hrPeak);
    const oneMin = parseFloat(hrOneMin);

    if (!rest || !peak || !oneMin || rest <= 0 || peak <= 0 || oneMin <= 0) {
      toast.warn(t("messages.enterValidValues"));
      return;
    }

    const vo2 = 15.3 * (peak / rest);
    const recovery = peak - oneMin;

    setVo2Max(vo2.toFixed(1));
    setRecoveryScore(recovery);

    let recoveryLabel = t("recovery.poor");
    if (recovery >= 30) {
      recoveryLabel = t("recovery.excellent");
    } else if (recovery >= 20) {
      recoveryLabel = t("recovery.good");
    } else {
      recoveryLabel = t("recovery.poor");
    }

    setRecoveryQuality(recoveryLabel);

    toast.success(t("messages.calculateSuccess"));
    return {
      result: vo2.toFixed(1),
      recovery,
    };
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return t("messages.notCalculated");
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatHistoryEntry = (entry) => {
    const createdAt = new Date(entry.createdAt).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return (
      <div className="d-flex justify-content-between">
        <p className="mb-1">{createdAt}</p>
        <p className="mb-0 fw-bold">{entry.result} ml/kg/min</p>
      </div>
    );
  };

  const generateAICoach = async (inputResult) => {
    try {
      // if (!courseId || !sectionId) return;

      setAiLoading(true);

      const aiPayload = {
        courseid: courseId,
        sectionid: sectionId,
        learnerid: user._id,
        type: "Cardio Test",
        result: [
          {
            hrBeforeExercise: `${inputResult.hrbefore} bpm` || null,
            hrAfterExercise: `${inputResult.hrafter} bpm` || null,
            hrOneMinAfterExercise: `${inputResult.hrminafter} bpm` || null,
            result: `${inputResult.result} ml/kg/min`,
          },
        ],
      };

      const res = await createAISuggestion({
        payload: aiPayload,
        params: { lang },
      }).unwrap();

      const suggestion = res?.data?.suggestion;
      if (suggestion) setAiSuggestion(suggestion);
    } catch (err) {
      console.log("AI error", err);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveResult = async (type, result) => {
    if (!result?.result) {
      toast.warn(t("messages.calculateFirst"));
      return;
    }

    if (!user) {
      toast.warn(t("messages.loginFirst"));
      // navigate("/auth/login");
      return;
    }

    try {
      const payload = {
        hrbefore: hrResting,
        hrafter: hrPeak,
        hrminafter: hrOneMin,
        result: result.result,
      };
      if (courseId) payload.course = courseId;
      const response = await createCardio({
        payload,
        params: { lang },
      }).unwrap();
      toast.success(response.message || t("messages.saveSuccess"));
      refetchCardio();
      refetchCardioData();
      // if (courseId && sectionId && templateId) {
      await generateAICoach(payload);
      // }
    } catch (error) {
      console.error("Error saving Cardio:", error);
      toast.error(t("messages.saveError"));
    }
  };

  const handleDeleteLast = async () => {
    try {
      const latestEntry = [...historyData].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      )[0];
      if (latestEntry) {
        const payload = {
          id: latestEntry._id,
          isactive: false,
          params: { lang },
        };
        await updateCardio(payload).unwrap();
        toast.success(t("messages.deleteSuccess"));
        refetchCardio();
        refetchCardioData();
      }
    } catch (error) {
      console.error("Error deleting last Cardio:", error);
      toast.error(t("messages.deleteError"));
    }
  };

  const toggleHistory = () => setShowHistory((prev) => !prev);

  if (isLoading || loadingCardio) {
    return <Loader loading={true} />;
  }

  return (
    <>
      <Helmet>
        <title>
          Cardio Fitness Test | Measure Cardiovascular Endurance | Traineego
        </title>

        <meta
          name="description"
          content="Assess your cardiovascular fitness and endurance level with Traineego's Cardio Fitness Test."
        />

        <meta name="robots" content="index,follow" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Cardio Fitness Test",
            url: "https://traineego.com/calculators/cardio",
            description:
              "Assess your cardiovascular fitness and endurance level with Traineego's Cardio Fitness Test.",
            publisher: {
              "@type": "Organization",
              name: "Traineego",
              url: "https://traineego.com",
            },
          })}
        </script>
      </Helmet>
      <Header />
      <section className="banner" style={{ textAlign: "left" }}>
        <div className="container py-5">
          <div className="row">
            <div className="col-12 text-center">
              <h1 className="text-uppercase">{t("title")}</h1>
              <p className="mb-0 p-lg l-grey-text">{t("subtitle")}</p>
            </div>
          </div>

          <div className="row d-flex">
            <div className="col-md-12 mt-4 p-5 fitness-card">
              <div className="row">
                <div className="col-lg-8">
                  <h4 className="text-uppercase mb-4">{t("testTitle")}</h4>

                  <Input
                    label={t("inputs.resting")}
                    placeholder={t("inputs.restingPlaceholder")}
                    type="number"
                    value={hrResting}
                    onChange={(e) => setHrResting(e.target.value)}
                    required
                  />
                  <Input
                    label={t("inputs.peak")}
                    placeholder={t("inputs.peakPlaceholder")}
                    type="number"
                    value={hrPeak}
                    onChange={(e) => setHrPeak(e.target.value)}
                    required
                  />
                  <Input
                    label={t("inputs.oneMinute")}
                    placeholder={t("inputs.oneMinutePlaceholder")}
                    type="number"
                    value={hrOneMin}
                    onChange={(e) => setHrOneMin(e.target.value)}
                    required
                  />

                  <div className="row mt-4">
                    <div className="fitness-branded col-12 py-2 px-4 d-flex justify-content-between align-items-center">
                      <p className="grey-text mb-0">{t("calculatePrompt")}</p>
                      <Button
                        text={t("calculateBtn")}
                        onClick={calculateCardioFitness}
                      />
                    </div>
                  </div>
                  {vo2Max && location.state?.returnTo && (
                    <div className="text-center mt-3">
                      <Button
                        text={t("continueLearningBtn")}
                        className="site-btn border-btn"
                        style={{ width: "100%", maxWidth: "200px" }}
                        onClick={() =>
                          navigate(location.state.returnTo, {
                            state: {
                              ...location.state,
                              resultKey: `${location.state.sectionIndex}-${location.state.lectureIndex}-CARDIO`,
                              resultData: {
                                type: "CARDIO",
                                result: vo2Max,
                                value: `${recoveryScore} bpm – ${recoveryQuality}`,
                                timestamp: new Date().toISOString(),
                              },
                            },
                          })
                        }
                      />
                    </div>
                  )}
                </div>

                <div className="col-12 col-lg-4 mt-4 mt-lg-0">
                  <div
                    className="h-100 p-3 p-md-4 d-flex flex-column justify-content-center"
                    style={{
                      border: "1px solid white",
                      borderRadius: "1rem",
                      background: "rgba(112,112,112,0.4)",
                    }}
                  >
                    {vo2Max && (
                      <>
                        <h6>
                          {t("results.vo2Max")}: {vo2Max} ml/kg/min
                        </h6>
                        <p className="p-sm">{t("results.vo2Info")}</p>
                      </>
                    )}
                    {recoveryScore !== null && (
                      <>
                        <p className="mt-2 fw-bold">
                          {t("results.recoveryScore")} {recoveryScore} bpm –{" "}
                          {recoveryQuality}
                        </p>
                      </>
                    )}
                    <p className="p-sm">{t("results.recoveryInfo")}</p>
                    <div
                      className="bg-white w-100 p-3 text-center mt-3"
                      style={{
                        border: "0.5px solid gray",
                        borderRadius: "0.5rem",
                      }}
                    >
                      <p className="m-0 grey-text p-sm">{t("results.tip")}</p>
                      <Button text={t("results.startBtn")} to="/classes" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <FitnessToolsResultCard
              type="Cardio"
              result={
                vo2Max
                  ? {
                      result: vo2Max,
                      recoveryScore,
                      timestamp: new Date().toISOString(),
                    }
                  : null
              }
              historyData={historyData}
              showHistory={showHistory}
              toggleHistory={toggleHistory}
              handleSaveResult={handleSaveResult}
              handleDeleteLast={handleDeleteLast}
              formatHistoryEntry={formatHistoryEntry}
              formatDateTime={formatDateTime}
              chartData={cardioData?.data || []}
              period={period}
              handlePeriodChange={handlePeriodChange}
            />

            {(aiLoading || aiSuggestion) && (
              <div
                className={`ai-suggestion-box ${highlightAI ? "ai-highlight" : ""}`}
                ref={aiRef}
              >
                <h4 className="text-white mb-3">
                  {/* <FontAwesomeIcon icon={faRobot} color="var(--primaryColor)" />{" "} */}
                  <img
                    src="https://traineego.com/assets/images/favicon-2026.ico"
                    alt="AI Coach"
                    style={{
                      width: "45px",
                      marginRight: "8px",
                      borderRadius: "25px",
                    }}
                  />
                  {t("aiSuggestion.title")}
                </h4>

                {showSkeleton ? (
                  // <p style={{ color: "#aaa" }}>
                  //   <Spinner size="sm" /> {t("aiSuggestion.loading")}
                  // </p>
                  <AISkeleton />
                ) : (
                  <div
                    style={{ color: "#d1d1d6", lineHeight: "1.7" }}
                    dangerouslySetInnerHTML={{
                      __html: formatAISuggestion(aiSuggestion),
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      <OtherFitnessTestSection
        data={FITNESSTEST.filter(
          (item) => item.route !== window.location.pathname,
        )}
      />
      <Footer />
    </>
  );
};

export default CardioFitnessTest;
